/**
 * React Query hooks for per-user Email Signature CRUD.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@aaas/shared';
import emailSignatureService from '../services/email-signature.service';
import type { EmailSignatureCreateInput, EmailSignatureUpdateInput } from '../types';

interface UseEmailSignatureListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  enabled?: boolean;
}

export const useEmailSignatureListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  enabled = true,
}: UseEmailSignatureListQueryParams) => {
  const skip = (page - 1) * size;
  return useQuery({
    queryKey: ['email-signatures', workspaceId, page, size],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return await emailSignatureService.listSignatures(workspaceId, idToken, {
        skip,
        limit: size,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateEmailSignatureMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (data: EmailSignatureCreateInput) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailSignatureService.createSignature(
        currentWorkspace.id,
        idToken,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-signatures'] });
    },
  });
};

export const useUpdateEmailSignatureMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async ({
      signatureId,
      data,
    }: {
      signatureId: string;
      data: EmailSignatureUpdateInput;
    }) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailSignatureService.updateSignature(
        signatureId,
        currentWorkspace.id,
        idToken,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-signatures'] });
    },
  });
};

export const useSetDefaultEmailSignatureMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (signatureId: string) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      return await emailSignatureService.setDefault(
        signatureId,
        currentWorkspace.id,
        idToken
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-signatures'] });
    },
  });
};

export const useDeleteEmailSignatureMutation = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace, idToken } = useAuth();

  return useMutation({
    mutationFn: async (signatureId: string) => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and token are required');
      }
      await emailSignatureService.deleteSignature(
        signatureId,
        currentWorkspace.id,
        idToken
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-signatures'] });
    },
  });
};
